<script lang="ts" setup>
import { resolveModalTeleportTarget, type ModalTeleportTarget } from '../ui-modal/teleportTarget';

defineOptions({
    name: 'IdeoTooltip',
    inheritAttrs: false
});

const props = withDefaults(defineProps<{
    tooltip?: string | null;
    position?: 'top' | 'bottom' | 'left' | 'right';
    multiline?: boolean;
    variant?: 'compact' | 'rich' | 'version';
    allowContentHover?: boolean;
    disabled?: boolean;
    tag?: string;
    maxWidth?: number;
    target?: HTMLElement | null;
    external?: boolean;
}>(), {
    tooltip: '',
    position: 'top',
    multiline: false,
    variant: 'compact',
    allowContentHover: false,
    disabled: false,
    tag: 'span',
    maxWidth: undefined,
    target: null,
    external: false
});

const slots = useSlots();
const wrapper = ref<HTMLElement | null>(null);
const popover = ref<any>(null);
const teleportTo = ref<ModalTeleportTarget>('body');
const tooltipId = `ideo-tooltip-${useId().replaceAll(':', '')}`;

let describedElement: HTMLElement | null = null;
let previousDescription: string | null = null;

const hasContent = computed(() => !props.disabled && (Boolean(props.tooltip) || Boolean(slots.content)));
const resolvedTarget = computed(() => props.target || wrapper.value);
const maxWidth = computed(() => props.maxWidth ?? (props.variant === 'version' ? undefined : props.variant === 'rich' || props.multiline ? 288 : 320));
const contentClasses = computed(() =>
{
    if (props.variant === 'version')
    {
        return [
            '!z-[400]',
            '!w-auto',
            '!min-w-[156px]',
            '!max-w-[calc(100vw-24px)]',
            '!whitespace-normal',
            '!rounded-[8px]',
            '!border-0',
            '!bg-side-bg',
            '!px-[11px]',
            '!py-[9px]',
            '!text-start',
            '!text-[0.75rem]/[1.45]',
            '!font-[400]',
            '!text-side-fg',
            '!opacity-0',
            '!scale-100',
            '!translate-x-0',
            '!translate-y-0',
            '!shadow-[0_8px_24px_-6px_oklch(0_0_0/0.35)]',
            '[transition:opacity_0.12s_ease]',
            '[&.is-open]:!opacity-100',
            '[&.is-open]:[transition:opacity_0.12s_ease]',
            'dark:!bg-[oklch(0.97_0_0)]',
            'dark:!text-[oklch(0.2_0.02_260)]',
            'motion-reduce:!transition-none'
        ];
    }

    if (props.variant === 'rich' || props.multiline)
    {
        return [
            '!z-[400]',
            '!w-max',
            '!whitespace-normal',
            '[overflow-wrap:anywhere]',
            '!rounded-[8px]',
            '!border-0',
            '!bg-side-bg',
            '!px-[11px]',
            '!py-[8px]',
            '!text-start',
            '!text-[0.75rem]/[1.45]',
            '!font-[400]',
            '!text-side-fg',
            '!opacity-0',
            '!scale-100',
            '!translate-x-0',
            '!translate-y-0',
            '!shadow-[0_8px_24px_-6px_oklch(0_0_0/0.35)]',
            '[transition:opacity_0.12s_ease]',
            '[&.is-open]:!opacity-100',
            '[&.is-open]:[transition:opacity_0.12s_ease]',
            '[&_.eyebrow]:!text-side-muted',
            '[&_.faint]:!text-side-muted',
            '[&_.muted]:!text-side-muted',
            '[&_strong]:!text-side-fg',
            '[&_b]:!text-side-fg',
            'dark:!bg-[oklch(0.97_0_0)]',
            'dark:!text-[oklch(0.2_0.02_260)]',
            'dark:[&_strong]:!text-[oklch(0.2_0.02_260)]',
            'dark:[&_b]:!text-[oklch(0.2_0.02_260)]',
            'motion-reduce:!transition-none'
        ];
    }

    return [
        '!z-[400]',
        '!w-max',
        '!whitespace-normal',
        '[overflow-wrap:anywhere]',
        '!rounded-[8px]',
        '!border-0',
        '!bg-side-bg',
        '!px-[9px]',
        '!py-[6px]',
        '!text-center',
        '!text-[0.6875rem]/none',
        '!font-[500]',
        '!text-side-fg',
        '!opacity-0',
        '!scale-100',
        '!shadow-none',
        '[&.ideo-floating--placement-top]:!translate-y-[5px]',
        '[&.ideo-floating--placement-bottom]:!-translate-y-[5px]',
        '[&.ideo-floating--placement-left]:!translate-x-[5px]',
        '[&.ideo-floating--placement-right]:!-translate-x-[5px]',
        '[transition:opacity_0.13s_ease,translate_0.13s_ease]',
        '[&.is-open]:!translate-x-0',
        '[&.is-open]:!translate-y-0',
        '[&.is-open]:!opacity-100',
        '[&.is-open]:[transition:opacity_0.14s_cubic-bezier(.2,.9,.3,1.1),translate_0.14s_cubic-bezier(.2,.9,.3,1.1)]',
        'dark:!bg-[oklch(0.97_0_0)]',
        'dark:!text-[oklch(0.2_0.02_260)]',
        'motion-reduce:!translate-x-0',
        'motion-reduce:!translate-y-0',
        'motion-reduce:!transition-none'
    ];
});

function focusableElement()
{
    const target = resolvedTarget.value;

    if (!target)
        return null;

    if (target.matches('button,a,input,select,textarea,[tabindex]'))
        return target;

    return target.querySelector<HTMLElement>('button,a,input,select,textarea,[tabindex]');
}

function restoreDescription()
{
    if (!describedElement)
        return;

    if (previousDescription)
        describedElement.setAttribute('aria-describedby', previousDescription);
    else
        describedElement.removeAttribute('aria-describedby');

    describedElement = null;
    previousDescription = null;
}

let disposed = false;

async function syncDescription()
{
    if (disposed)
        return;

    restoreDescription();

    if (!hasContent.value)
        return;

    await nextTick();
    if (disposed)
        return;

    describedElement = focusableElement();

    if (!describedElement)
        return;

    previousDescription = describedElement.getAttribute('aria-describedby');

    const currentDescriptions = (previousDescription || '').split(/\s+/).filter(Boolean);
    const descriptions = new Set([...currentDescriptions, tooltipId]);

    describedElement.setAttribute('aria-describedby', [...descriptions].join(' '));
}

function onKeydown(event: KeyboardEvent)
{
    if (event.key === 'Escape')
        popover.value?.close();
}

onMounted(async () =>
{
    teleportTo.value = resolveModalTeleportTarget(resolvedTarget.value);
    await syncDescription();
    if (!disposed)
        document.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() =>
{
    disposed = true;
    restoreDescription();
    document.removeEventListener('keydown', onKeydown);
});

watch(hasContent, syncDescription);
watch(() => props.target, async () =>
{
    teleportTo.value = resolveModalTeleportTarget(resolvedTarget.value);
    await syncDescription();
});

defineExpose({
    open: () => popover.value?.open(),
    close: () => popover.value?.close()
});
</script>

<template>
    <component v-if="!props.external" :is="props.tag" ref="wrapper" class="inline-flex min-w-0 max-w-full" v-bind="$attrs">
        <slot />
    </component>
    <Teleport :to="teleportTo">
        <IdeoPopover
            v-if="hasContent"
            :id="tooltipId"
            ref="popover"
            :target="resolvedTarget"
            :placement="props.position"
            :distance="8"
            :arrow="false"
            :body="false"
            :capture-hover="props.allowContentHover"
            :hover-close-delay="0"
            :lazy="false"
            :max-width="maxWidth"
            :class="contentClasses"
            triggers="hover focus"
            variant="tooltip"
            role="tooltip"
        >
            <slot name="content">
                {{ props.tooltip }}
            </slot>
        </IdeoPopover>
    </Teleport>
</template>
